<template>
  <div>
    <b-sidebar
      id="sidebar-checklist-fase1"
      sidebar-class="sidebar-lg"
      :visible="checklistFase1Sidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateChecklistFase1Sidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>Checklist</h4>
          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <b-form class="p-2" @submit.prevent="onSubmit" @reset.prevent="resetForm">
          <template v-if="items.length">
            <div
              v-for="(it, idx) in items"
              :key="it.item_id"
              class="mb-2"
            >
              <b-form-group :label="it.name" :description="it.help_text || null">
                <!-- boolean -->
                <b-form-radio-group
                  v-if="it.type === 'boolean'"
                  v-model="it.uiValue"
                  :options="simNaoOptions"
                />

                <!-- number -->
                <b-form-input
                  v-else-if="it.type === 'number'"
                  v-model.number="it.uiValue"
                  type="number"
                  :min="it.min"
                  :max="it.max"
                  :placeholder="placeholderFor(it)"
                />

                <!-- scale (numeric, constrained by min/max) -->
                <b-form-select
                  v-else-if="it.type === 'scale'"
                  v-model.number="it.uiValue"
                  :options="scaleOptions(it)"
                />

                <!-- text -->
                <b-form-textarea
                  v-else-if="it.type === 'text'"
                  v-model="it.uiValue"
                  rows="3"
                  :placeholder="placeholderFor(it)"
                />

                <!-- single_select (simple string value; you can replace with real options later) -->
                <b-form-input
                  v-else-if="it.type === 'single_select'"
                  v-model="it.uiValue"
                  :placeholder="placeholderFor(it)"
                />

                <!-- multi_select (comma-separated for now; adapt to tags/multiselect later) -->
                <b-form-input
                  v-else-if="it.type === 'multi_select'"
                  v-model="it.uiValue"
                  :placeholder="placeholderFor(it, 'Separe por vírgulas')"
                />

                <!-- unsupported (shouldn't happen; backend guards, but just in case) -->
                <div v-else class="text-danger small">Tipo não suportado: {{ it.type }}</div>
              </b-form-group>
            </div>
          </template>

          <div v-else class="p-2 text-muted">Nenhum item disponível para este checklist.</div>

          <!-- Actions -->
          <div class="d-flex mt-2">
            <b-button :disabled="saving" v-ripple.400="'rgba(255,255,255,0.15)'" variant="primary" class="mr-2" type="submit">
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </b-button>
            <b-button variant="outline-secondary" type="reset">Limpar</b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup, BButton,
} from "bootstrap-vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import Ripple from "vue-ripple-directive";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";

export default {
  name: "ChecklistSidebarDynamic",
  components: { BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup, BButton },
  directives: { Ripple },
  setup() { return { toast: useToast() }; },
  data() {
    return {
      saving: false,
      items: [], // ← dynamic [{ item_id, name, type, min, max, help_text, uiValue, ... }]
      simNaoOptions: [
        { text: "Sim", value: true },
        { text: "Não", value: false },
      ],
    };
  },
  computed: {
    ...mapGetters({ checklistFase1Sidebar: types.CHECKLIST_FASE1_SIDEBAR }),
  },
  methods: {
    ...mapMutations({ mutateChecklistFase1Sidebar: types.MUTATE_CHECKLIST_FASE1_SIDEBAR }),
    ...mapActions({ getLeadChecklist: types.GET_LEAD_CHECKLIST, saveLeadChecklist: types.SAVE_LEAD_CHECKLIST }),

    placeholderFor(it, extra = "") {
      if (it.type === "number") return "Digite um número";
      if (it.type === "text") return "Digite um texto";
      if (extra) return extra;
      return "";
    },

    scaleOptions(it) {
      const min = Number(it.min ?? 0);
      const max = Number(it.max ?? 10);
      const opts = [{ value: null, text: "Selecione" }];
      for (let v = min; v <= max; v++) opts.push({ value: v, text: String(v) });
      return opts;
    },

    // Convert API answer → UI value
    toUiValue(item) {
      const ans = item.answer || {};
      switch (item.type) {
        case "boolean": return ans.checked ?? null;
        case "number":  return ans.number_value ?? null;
        case "scale":   return ans.number_value ?? null;
        case "text":    return ans.text_value ?? "";
        case "single_select": return ans.text_value ?? "";
        case "multi_select":  return ans.text_value ?? ""; // comma-separated for now
        default: return null;
      }
    },

    // Convert UI value → backend "value"
    toApiValue(item) {
      switch (item.type) {
        case "boolean": return item.uiValue;
        case "number":
        case "scale":   return item.uiValue === null || item.uiValue === "" ? null : Number(item.uiValue);
        case "text":
        case "single_select":
        case "multi_select": return item.uiValue ?? "";
        default: return null;
      }
    },

    // Load items for checklist (server decides phases)
    loadData() {
      const customerId = this.$route.params.id;
      if (!customerId) return;

      this.getLeadChecklist({ customerId, code: "EFFICIENCY_EA" })
        .then((res) => {
          const items = (res.data && res.data.items) ? res.data.items : [];
          // Decorate with uiValue
          this.items = items.map(it => ({ ...it, uiValue: this.toUiValue(it) }));
        })
        .catch((e) => {
          console.error("Erro ao carregar checklist:", e);
          this.items = [];
        });
    },

    onSubmit() {
      this.saving = true;
      const customerId = this.$route.params.id;
      if (!customerId) { this.saving = false; return; }

      // Build answers only for items that the user actually set (allow nulls for boolean false, etc.)
      const answers = [];
      for (const it of this.items) {
        const val = this.toApiValue(it);
        // Decide your rule for "send or not". Here we send when val is not null/empty string.
        const shouldSend =
          (it.type === "text" || it.type === "single_select" || it.type === "multi_select")
            ? (val !== null && val !== "")
            : (val !== null && val !== undefined);

        if (shouldSend) answers.push({ item_id: it.item_id, value: val });
      }

      const payload = {
        customer_id: customerId,
        code: "EFFICIENCY_EA", // IMPORTANT: backend expects the parent code, not the phase code
        answers,
      };

      this.saveLeadChecklist(payload)
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: { title: "Sucesso", text: "Checklist salvo com sucesso", icon: "CheckIcon", variant: "success" },
          });
          this.mutateChecklistFase1Sidebar({ visible: false });
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: { title: "Erro", text: "Erro ao salvar checklist. Tente novamente.", icon: "AlertTriangleIcon", variant: "danger" },
          });
        })
        .finally(() => { this.saving = false; });
    },

    resetForm() {
      this.items = this.items.map(it => ({ ...it, uiValue: this.toUiValue({ ...it, answer: {} }) }));
    },

    clear() { this.resetForm(); },
  },

  mounted() {
    if (this.checklistFase1Sidebar.visible) this.loadData();
  },

  watch: {
    "checklistFase1Sidebar.visible"(v) { if (v) this.loadData(); },
  },
};
</script>

<style lang="scss" scoped>
.content-sidebar-header { border-bottom: 1px solid #e9ecef; }
</style>
